import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { Button } from './Button'

/**
 * Copy to the clipboard, saying so.
 *
 * Lifted from the sender step (S-B18) when Settings → Branding and Settings →
 * Domain needed the same button beside a workspace URL and two DNS records;
 * three copies of a two-second timer is three chances to leak one.
 *
 * `navigator.clipboard` is absent in insecure contexts and in jsdom, so the
 * failure path leaves the button unchanged rather than throwing — the value is
 * on screen and can still be selected by hand.
 *
 * `label` names what is copied for a screen reader, because a table of three
 * *Copy* buttons is three identical controls otherwise.
 */
export function CopyButton({ value, label }: { value: string; label?: string }): JSX.Element {
  const [copied, setCopied] = useState(false)
  // WEB-13. The revert was scheduled with no cleanup, so pressing *Continue*
  // inside the two seconds set state on an unmounted component. Held in a ref
  // rather than in state: nothing renders from it, and a state update here
  // would restart the very timer it is trying to track.
  const revert = useRef<number | undefined>(undefined)

  useEffect(
    () => () => {
      if (revert.current !== undefined) window.clearTimeout(revert.current)
    },
    [],
  )

  return (
    <Button
      tone="quiet"
      size="sm"
      aria-label={label === undefined ? undefined : `${copied ? 'Copied' : 'Copy'} ${label}`}
      onClick={() => {
        // Typed as always present, and it is not: absent in an insecure context
        // and in jsdom. The cast is what lets the honest check compile.
        const clipboard = navigator.clipboard as Clipboard | undefined
        void clipboard
          ?.writeText(value)
          .then(() => {
            setCopied(true)
            // Copying a second value inside the window would otherwise leave
            // the first timer to clear the second one's "Copied" early.
            if (revert.current !== undefined) window.clearTimeout(revert.current)
            revert.current = window.setTimeout(() => {
              setCopied(false)
            }, 2000)
          })
          .catch(() => {
            /* No clipboard permission. The value is visible and selectable. */
          })
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  )
}
